import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = ({ variant = "default", title = "İletişime Geçin" }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone) {
      toast.error("Lütfen adınızı ve telefon numaranızı girin.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Mesajınız alındı! En kısa sürede sizinle iletişime geçeceğiz.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("Bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const isCompact = variant === "compact";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={`${
        isCompact
          ? "bg-white rounded-2xl border border-slate-200 shadow-lg p-6"
          : "bg-slate-50 rounded-3xl p-8"
      }`}
    >
      {title && (
        <h3 className={`font-bold font-['Outfit'] mb-6 ${isCompact ? "text-xl" : "text-2xl"}`}>
          {title}
        </h3>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
        <div>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Ad Soyad *"
            className="bg-white border-slate-200 focus:border-purple-500 focus:ring-purple-500"
            data-testid="contact-form-name"
          />
        </div>

        <div>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="E-posta"
            className="bg-white border-slate-200 focus:border-purple-500 focus:ring-purple-500"
            data-testid="contact-form-email"
          />
        </div>

        <div>
          <Input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="Telefon *"
            className="bg-white border-slate-200 focus:border-purple-500 focus:ring-purple-500"
            data-testid="contact-form-phone"
          />
        </div>

        <div>
          <Textarea
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            placeholder="Mesajınız"
            rows={isCompact ? 3 : 4}
            className="bg-white border-slate-200 focus:border-purple-500 focus:ring-purple-500 resize-none"
            data-testid="contact-form-message"
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
          data-testid="contact-form-submit"
        >
          {loading ? (
            <Loader2 className="animate-spin mr-2" size={20} />
          ) : (
            <Send size={20} className="mr-2" />
          )}
          {loading ? "Gönderiliyor..." : "Gönder"}
        </Button>

        <p className="text-center text-sm text-slate-500">
          1 saat içinde dönüş yapıyoruz.
        </p>
      </form>
    </motion.div>
  );
};

export default ContactForm;
